<template lang="html">
  <div>
    <!-- Single option, except if only option is delete -->
    <v-overlay :value="active" opacity="0.4" />

    <portal
      to="footer-additional-options"
      v-if="menuOptions.length === 1 && menuOptions[0].name !== 'delete'"
    >
      <v-btn
        :href="menuOptions[0].href"
        @click.stop="handleClick(menuOptions[0])"
        color="secondary"
        absolute
        top
        right
        dark
        fab
      >
        <v-icon class="main-icon">
          {{ menuOptions[0].icon }}
        </v-icon>
      </v-btn>
    </portal>

    <!-- Multiple options -->
    <v-speed-dial
      v-else-if="menuOptions.length"
      class="is-downshifted on-overlay"
      transition="slide-y-reverse-transition"
      absolute
      bottom
      right
      v-model="active"
    >
      <template #activator>
        <v-btn v-model="active" color="secondary" dark fab>
          <v-icon class="main-icon" :class="{ 'main-icon--active': active }">
            mdi-chevron-up
          </v-icon>
        </v-btn>
      </template>

      <template>
        <BaseActionMenuButton
          v-for="option in menuOptions"
          :key="'option_menu_item_' + option.name"
          :icon="option.icon || 'mdi-circle-outline'"
          v-bind="option"
          @click.stop="handleClick(option)"
        />
      </template>
    </v-speed-dial>

    <!-- Delete dialog -->
    <v-dialog v-model="destroy" max-width="400" persistent>
      <v-card>
        <v-card-title class="headline">
          {{ capitalize($t('general.remove')) }}
        </v-card-title>
        <v-card-text>
          {{ $t('general.are_you_sure') }}
        </v-card-text>
        <v-card-actions>
          <BaseDialogActions
            @cancel="destroy = false"
            @continue="
              $emit('click:delete');
              destroy = false;
            "
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { capitalize } from 'lodash';

export default {
  name: 'BaseActionMenu',
  props: {
    //  Use these with v-bind="meta" shorthand to automatically enable all permissions of the person
    canCreate: {
      type: Boolean,
      default: false,
    },
    canDestroy: {
      type: Boolean,
      default: false,
    },
    canUpdate: {
      type: Boolean,
      default: false,
    },
    additionalOptions: {
      // describes an array of additional action buttons for the list
      type: Array,
      default: () => [],

      // items should be like this:
      // {
      //   name: String,
      //   icon?: String,
      //   label?: String, (or Function returning String)
      //   callback?: Function,
      //   active?: Boolean,
      //   ... or any v-btn props https://vuetifyjs.com/en/api/v-btn/
      // }
    },
  },
  data() {
    return {
      active: false,
      destroy: false,
    };
  },
  computed: {
    menuOptions() {
      const map = new Map();
      const options = [
        {
          name: 'add',
          icon: 'mdi-plus',
          label: this.$t('general.add'),
          active: this.canCreate,
        },
        {
          name: 'edit',
          icon: 'mdi-pencil',
          label: this.$t('general.edit'),
          active: this.canUpdate,
        },

        ...this.additionalOptions,

        {
          // Always put delete last, after other options. Prevents override by user.
          name: 'delete',
          icon: 'mdi-trash-can-outline',
          label: this.$t('general.remove'),
          callback: () => (this.destroy = true),
          active: this.canDestroy,
        },
      ];

      options.forEach(option => map.set(option.name, option)); // overwrite duplicate names while maintaining array order

      const result = Array.from(map.values()).filter(
        x => x.active || !Object.prototype.hasOwnProperty.call(x, 'active') // if key does not exist, assume the option should display
      );

      return result;
    },
  },
  methods: {
    capitalize,
    handleClick(option) {
      if (option.name && option.name !== 'delete') {
        // click:delete emits on popup window confirmation, not automatically.
        // Maybe refactor to 2 events: 'click:delete' for clicking the button and ('delete' or 'confirm:delete') for confirming the dialog.
        this.$emit('click:' + option.name);
        this.active = false;
      }
      if (option.callback) {
        option.callback();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.is-downshifted {
  transform: translateY(0%);
}

.main-icon {
  transition: transform 0.2s ease;
  transform: rotate(0deg);

  &--active {
    transform: rotate(-180deg);
  }
}

.v-speed-dial.on-overlay {
  z-index: 10;
}

.v-speed-dial.is-downshifted {
  transform: translateY(75%);
}
</style>
